import { useCallback, useEffect, useRef, useState } from "react";

export interface UseIntervalOptions {
    /** disable the interval */
    disable?: boolean;
    /** run the interval immediately. Defaults to true. */
    runImmediately?: boolean;
    /** Time in MS to wait before the first interval. Defaults to 0. */
    predelay?: number;
    /** Amount of time in milliseconds to run before stopping. Not set by default. */
    timeout?: number;
    /** Called when disable is true */
    onDisable?: () => void;
    /** Called when timeout is hit */
    onTimeout?: () => void;
}

export function useInterval(
    callback: () => void,
    delay: number = 1000,
    {
        disable,
        runImmediately = true,
        predelay = 0,
        timeout,
        onDisable,
        onTimeout
    }: UseIntervalOptions = {
        disable: false
    }
) {
    const intervalId = useRef<NodeJS.Timeout>(null);
    const savedCallback = useRef<() => void>(null);
    const startedAt = useRef<number>(null);
    const lastExecutedAt = useRef<number>(null);
    const prevDisableRef = useRef(disable);

    const [running, setRunning] = useState<boolean>(false);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    const initialize = useCallback(() => {
        if (!running && !disable) {
            setTimeout(() => {
                setRunning(true);
                startedAt.current = new Date().getTime();
                lastExecutedAt.current = runImmediately
                    ? 0
                    : new Date().getTime();
            }, predelay);
        }
    }, [predelay, running, disable, runImmediately]);

    const clear = useCallback(() => {
        if (intervalId.current) {
            setRunning(false);
            clearTimeout(intervalId.current);
            intervalId.current = null;
        }
    }, []);

    // If disable becomes true and we have executed at least once, clear the interval and run disable callback
    useEffect(() => {
        // Only trigger when disable CHANGES from false to true
        if (disable && !prevDisableRef.current && !!lastExecutedAt.current) {
            clear();
            onDisable?.();
        }

        // Update the ref for next comparison
        prevDisableRef.current = disable;
    }, [disable, clear, onDisable]);

    // Don't auto-start if disabled
    useEffect(() => {
        if (runImmediately && !disable) {
            initialize();
        }
    }, [initialize, runImmediately, disable]);

    useEffect(() => {
        function tick() {
            setRunning(true);
            const now = new Date().getTime();
            const timeElapsed = now - startedAt.current;

            if (timeout && timeElapsed >= timeout) {
                clear();
                onTimeout?.();
            } else {
                savedCallback.current();
                lastExecutedAt.current = now;
                intervalId.current = setTimeout(tick, delay);
            }
        }

        if (!disable && delay !== null && (running || lastExecutedAt.current)) {
            // Clear any existing timeout when delay changes
            if (intervalId.current) {
                clearTimeout(intervalId.current);
            }

            // Calculate time until next execution
            const now = new Date().getTime();
            const executionTime = lastExecutedAt.current || now;
            const timeUntilNextExecution = Math.max(
                0,
                delay - (now - executionTime)
            );

            intervalId.current = setTimeout(tick, timeUntilNextExecution);
        }

        return () => clear();
    }, [clear, delay, disable, running, timeout, onTimeout]);

    return { interval: intervalId.current, running, initialize, clear };
}
